/**
 * store/examFilterStore.ts
 *
 * WorksheetPage (the Exam tab) and its desktop rail card
 * (ExamChildRailCard) are two independent mount points under AppShell —
 * same relationship as PracticePage/AssignmentStatusRailCard — so the
 * syllabus filter has to live somewhere both can reach rather than being
 * prop-drilled through RootLayout.
 *
 * Behaviour rule (WP1 brief): picking a child on the rail sets the syllabus
 * AND marks that child as the rail's selection; changing the Syllabus
 * picker directly on the page also sets the syllabus, but clears the
 * selection instead — the picker always wins, and the rail's highlight only
 * ever reflects the last *rail* pick, never re-syncing to it afterwards.
 */

import { create } from 'zustand';
import type { Syllabus } from '@tupai/types';

interface ExamFilterState {
  syllabus: Syllabus;
  /** link_id of the child last picked from the rail card, or null once the
   *  Syllabus picker has been changed directly since (see file header). */
  selectedChildLinkId: string | null;
  /** WorksheetPage's own Syllabus picker — wins over any rail selection. */
  setSyllabus: (syllabus: Syllabus) => void;
  /** ExamChildRailCard row tap. */
  pickChild: (linkId: string, syllabus: Syllabus) => void;
  /** Logout (main.tsx) — a shared device must not land the next parent on
   *  the previous parent's syllabus filter or rail selection. */
  reset: () => void;
}

export const useExamFilterStore = create<ExamFilterState>((set) => ({
  syllabus: 'KSSM',
  selectedChildLinkId: null,
  setSyllabus: (syllabus) => set({ syllabus, selectedChildLinkId: null }),
  pickChild: (linkId, syllabus) => set({ syllabus, selectedChildLinkId: linkId }),
  reset: () => set({ syllabus: 'KSSM', selectedChildLinkId: null }),
}));
